'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { getTutorialUiContent } from '@/lib/ui-content/tutorial-ui-content';
import Image from 'next/image';

interface TutorialWelcomeProps {
  locale: LocaleDetails;
  onStart: () => void;
  onSkip: () => void;
}

export const TutorialWelcome = ({ locale, onStart, onSkip }: TutorialWelcomeProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const tutorialContent = getTutorialUiContent(locale);

  useEffect(() => {
    setMounted(true);
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => {
      clearTimeout(timer);
      setMounted(false);
    };
  }, []);

  const handleStart = () => {
    setIsVisible(false);
    setTimeout(() => onStart(), 300);
  };

  const handleSkip = () => {
    setIsVisible(false);
    setTimeout(() => onSkip(), 300);
  };

  if (!mounted) return null;

  return createPortal(
    <div className={`fixed inset-0 z-[6000] flex items-center justify-center p-4 transition-opacity duration-300 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {/* Backdrop - subtle blur for focus */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm dark:bg-black/60"
        onClick={handleSkip}
      />

      {/* Welcome Card - clean, modern, mobile-first */}
      <div
        className={`relative z-10 w-full max-w-sm transform rounded-2xl bg-white p-6 shadow-2xl transition-all duration-300 dark:bg-dark dark:border dark:border-transparent-dark sm:p-8 ${isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
          }`}
      >
        {/* Logo with subtle animation */}
        <div className="mb-6 flex justify-center">
          <div className="relative h-16 w-16 animate-fade-in sm:h-20 sm:w-20">
            <Image
              src="/images/inception-mark.png"
              alt="Inception"
              fill
              sizes="80px"
              priority
            />
          </div>
        </div>

        {/* Title - mono font to match brand */}
        <h2 className="mb-2 text-center font-mono text-xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-2xl">
          {tutorialContent.welcome}
        </h2>

        {/* Description */}
        <p className="mb-6 text-center text-sm text-neutral-600 dark:text-neutral-300 sm:mb-8 sm:text-base">
          {tutorialContent.regionStep.description}
        </p>

        {/* Features list - compact icons, clear hierarchy */}
        <div className="mb-6 space-y-3 sm:mb-8">
          <FeatureItem
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            }
            text={tutorialContent.searchSpotlight.searchBar.title}
          />
          <FeatureItem
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            text={tutorialContent.regionSpotlight.globe.title}
          />
          <FeatureItem
            icon={
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            text={tutorialContent.searchSpotlight.demo.title}
          />
        </div>

        {/* Action buttons - thumb-friendly sizing */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleStart}
            className="w-full rounded-xl bg-[#00a798] px-6 py-3 text-base font-semibold text-white shadow-lg transition-all hover:bg-[#008b7a] hover:shadow-xl active:scale-[0.98] sm:py-3.5"
          >
            {tutorialContent.nextButton}
          </button>
          <button
            onClick={handleSkip}
            className="py-2 text-sm font-medium text-neutral-500 transition-colors hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
          >
            {tutorialContent.skipButton}
          </button>
        </div>

        {/* Subtle corner decoration */}
        <div className="absolute -right-1 -top-1 h-16 w-16 rounded-full bg-[#00a798]/5 blur-2xl dark:bg-[#00a798]/10" />
        <div className="absolute -bottom-1 -left-1 h-12 w-12 rounded-full bg-[#00a798]/5 blur-xl dark:bg-[#00a798]/10" />
      </div>
    </div>,
    document.body
  );
};

// Feature item component for clean, reusable list items
const FeatureItem = ({ icon, text }: { icon: React.ReactNode; text: string }) => (
  <div className="flex items-center gap-3 text-sm text-neutral-700 dark:text-neutral-300">
    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-[#00a798]/10 text-[#00a798] dark:bg-[#00a798]/20">
      {icon}
    </div>
    <span className="font-medium">{text}</span>
  </div>
);
